import { useEffect, useState } from 'react';
import { useWindowSize } from 'react-use';

import dynamic from 'next/dynamic';

import { SwiperSlide } from 'swiper/react';

import { routes } from '@hultafors/hultaforsgroup/config';
import { useGlobal } from '@hultafors/hultaforsgroup/hooks';
import {
  BrandFragment,
  BrandsBlockFragment,
} from '@hultafors/hultaforsgroup/types';
import { breakpoints, breakpointsRaw } from '@hultafors/shared/constants';

import styles from './brands-block.module.scss';
import SwiperContent from './swiper-wrapper';

const HTag = dynamic(() =>
  import('@hultafors/shared/components').then((mod) => mod.HTag),
);

const Link = dynamic(() =>
  import('@hultafors/shared/components').then((module) => module.SharedLink),
);

const DatoImage = dynamic(
  () => import('@hultafors/shared/components').then((mod) => mod.DatoImage),
  { ssr: false },
);

const Paragraph = dynamic(() =>
  import('@hultafors/shared/components').then((module) => module.Paragraph),
);

type BrandsBlockProps = Omit<BrandsBlockFragment, 'id'>;

export const BrandsBlock: React.FC<BrandsBlockProps> = ({
  brands = [],
  hideLabels,
  introText,
  title,
}) => {
  const [sliderItems, setSliderItems] = useState<BrandsBlockProps['brands'][]>(
    [],
  );
  const { globalContent } = useGlobal();
  const { width } = useWindowSize();

  useEffect(() => {
    const getBrandsArr = () => {
      const arrayOfIndex: number[] = [];
      const arrayOfBrands: BrandsBlockProps['brands'][] = [];
      const itemsToShow = width > breakpointsRaw.tablet ? 6 : 3;

      for (let index = 0; index < brands.length; index++) {
        if (index % itemsToShow === 0) {
          arrayOfIndex.push(index);
        }
      }

      arrayOfIndex.map((index) => {
        const elements = brands.slice(index, index + itemsToShow);
        return arrayOfBrands.push(elements);
      });

      setSliderItems(arrayOfBrands);
    };

    getBrandsArr();
  }, [brands, width]);

  if (!title && !brands.length && !introText) {
    return null;
  }
  const sizes = [
    `(min-width: ${breakpoints.desktop}) 148px`,
    `(min-width: ${breakpoints.tablet}) 25vw`,
    '50vw',
  ].join(', ');
  const brandMapper = ({ brandName, imageLoad, slug }: BrandFragment) => (
    <Link
      className={styles['link']}
      href={{ pathname: `${routes.brands}/${slug}` }}
      isWrapper
      key={`key-${brandName}`}
    >
      <DatoImage
        alt={imageLoad?.alt || brandName || ''}
        aspectRatio={1}
        fill
        image={imageLoad}
        sizes={sizes}
      />
      {!hideLabels && (
        <Paragraph
          adjustStyles={{
            color: 'var(--color-white)',
            marginBlock: 'var(--spacing-small) 0',
            textAlign: 'center',
          }}
          styleType="sub-heading"
        >
          {brandName}
        </Paragraph>
      )}
    </Link>
  );

  return (
    <div className={styles['brands-block']}>
      <section
        className={styles['wrapper']}
        data-test="OurBrandsBlock"
        id="our-brands"
      >
        <HTag
          adjustStyles={{ color: 'var(--color-white)', textAlign: 'center' }}
          className={styles['title']}
          type="h2"
        >
          {title}
        </HTag>
        <SwiperContent
          ariaLabelNext={globalContent.nextLabel || 'Next'}
          ariaLabelPrevious={globalContent.previousLabel || 'Previous'}
        >
          {sliderItems.map((brandGroup, index) => (
            <SwiperSlide className="swiper-slide" key={`SliderBlock-${index}`}>
              <div className={styles['slide']}>
                {brandGroup.map(brandMapper)}
              </div>
            </SwiperSlide>
          ))}
        </SwiperContent>
      </section>
    </div>
  );
};
